/**
 * Agent Atlas Microservice API Client
 *
 * Uses the unified API client. Nginx routes:
 *   /agent-atlas/api/v1/*  → saf3ai-agent-atlas-microservice
 *
 * This microservice aggregates across every SAF3AI module (runtime traces,
 * red-team, policy, compliance, SAST, endpoint/shadow AI) on a unified schema.
 */

import api from './apiClient';

// ---------------------------------------------------------------------------
// Graph reads are served by saf3ai-graph-microservice, not agent-atlas.
//
// These delegate to the lens service so there is ONE read path over the entity graph.
// agent-atlas kept its own /graph/* endpoints for a while and that was the problem: three
// services read the graph, and hand-written Cypher in a consumer is what returned zero
// rows silently when the ontology moved.
//
// Kept as thin delegates rather than deleted so existing callers keep working; new code
// should import graphLensService and render the coverage envelope directly.
// ---------------------------------------------------------------------------
import { graphLensService } from '../graph/graphLensService';
import { withSurface } from '../security/surfaceScope';


const ATLAS_PREFIX = '/agent-atlas/api/v1';

export const agentAtlasAPI = {
  async getHealth() {
    return (await api.get(`${ATLAS_PREFIX}/health`)).data;
  },

  // ---- Surface manifests (config/surfaces reads these; call it through useSurfaces) ----
  async getSurfaceManifests() {
    return (await api.get(`${ATLAS_PREFIX}/surfaces`)).data;
  },

  // ---- Lens manifests (config/lenses reads these; call it through useLenses) ----
  async getLensManifests() {
    return (await api.get(`${ATLAS_PREFIX}/lenses`)).data;
  },

  // ---- Agent picker: Context Graph agents the caller may link to ----
  // Scoped server-side to the org's entitled modules AND the caller's view
  // grants. `forModule` names the module it is opened from (e.g. 'sast').
  async getAgentPicker({ forModule, q, key, limit } = {}) {
    const params = {};
    if (forModule) params.for_module = forModule;
    if (q) params.q = q;
    if (key) params.key = key;
    if (limit) params.limit = limit;
    return (await api.get(`${ATLAS_PREFIX}/agent-picker`, { params })).data;
  },

  // ---- Atlas (enterprise overview) ----
  async getOverview(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/overview`, { params })).data;
  },
  async getUniverse(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/universe`, { params })).data;
  },
  async getCoverage(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/coverage`, { params })).data;
  },
  async getAttention(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/attention`, { params })).data;
  },
  async getSeverityDistribution(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/severity-distribution`, { params })).data;
  },

  // ---- Atlas home: Today / Estate (2026-10-02) ----
  // Each takes {days} or {start_time, end_time} like the calls above. Shapes are
  // documented on the service wrappers in services/agent-atlas/agentAtlasService.
  // Every number on the home comes from the metrics engine (src/metrics); these are lists.
  async getNewAgents(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/new-agents`, { params })).data;
  },
  async getNewIdentities(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/new-identities`, { params })).data;
  },
  async getFeedsHealth(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/feeds/health`, { params })).data;
  },
  async getOwnerSources(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/owner-sources`, { params })).data;
  },

  // ---- Predictive cloud posture ----
  async getCloudRisk(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/cloud-risk`, { params })).data;
  },

  // ---- Agent onboarding (owner, purpose, criticality; steps are data) ----
  // The agent id goes in the query, not the path: Atlas ids carry a surface prefix
  // and can contain slashes.
  async getOnboardingSteps(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/onboarding/steps`, { params })).data;
  },
  async listOnboarding(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/onboarding/agents`, { params })).data;
  },
  async getAgentOnboarding(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/onboarding/agent`, { params: { ...params, agent_id: agentId } })).data;
  },
  async getOnboardingContext(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/onboarding/agent/context`, { params: { ...params, agent_id: agentId } })).data;
  },
  // Bundles: blocks from the manifests + the org's own, compose from the four Purpose answers,
  // save / archive an organization bundle, apply one to an agent.
  async getOnboardingBundles(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/onboarding/bundles`, { params })).data;
  },
  async composeOnboardingBundle(body) {
    return (await api.post(`${ATLAS_PREFIX}/onboarding/bundles/compose`, body)).data;
  },
  async saveOnboardingBundle(body, bundleId) {
    return bundleId
      ? (await api.put(`${ATLAS_PREFIX}/onboarding/bundles/${bundleId}`, body)).data
      : (await api.post(`${ATLAS_PREFIX}/onboarding/bundles`, body)).data;
  },
  async archiveOnboardingBundle(bundleId) {
    return (await api.delete(`${ATLAS_PREFIX}/onboarding/bundles/${bundleId}`)).data;
  },
  async applyOnboardingBundle(agentId, body) {
    return (await api.post(`${ATLAS_PREFIX}/onboarding/agent/bundle`, body, { params: { agent_id: agentId } })).data;
  },
  // Mark newly discovered agents as reviewed (they stop being New), or take a review back.
  // body: { agent_ids: [...] } for named agents, or { all_new: true, days | start_time + end_time }
  // for every agent that is new in the page's window.
  async reviewAgents(body) {
    return (await api.post(`${ATLAS_PREFIX}/onboarding/review`, body)).data;
  },
  async reopenAgentReview(body) {
    return (await api.post(`${ATLAS_PREFIX}/onboarding/review/reopen`, body)).data;
  },
  async claimAgent(agentId, body) {
    return (await api.post(`${ATLAS_PREFIX}/onboarding/agent/claim`, body, { params: { agent_id: agentId } })).data;
  },
  async saveOnboardingStep(agentId, stepId, body) {
    return (await api.put(`${ATLAS_PREFIX}/onboarding/agent/steps/${encodeURIComponent(stepId)}`, body,
      { params: { agent_id: agentId } })).data;
  },
  // body: { keys: [...setup item keys to do], agent_name }. The response carries setup_results.
  async completeOnboarding(agentId, body = {}) {
    return (await api.post(`${ATLAS_PREFIX}/onboarding/agent/complete`, body, { params: { agent_id: agentId } })).data;
  },
  // What completing onboarding sets up for this agent (items), and what it already did (results).
  async getOnboardingSetup(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/onboarding/agent/setup`, { params: { ...params, agent_id: agentId } })).data;
  },
  async runOnboardingSetup(agentId, body) {
    return (await api.post(`${ATLAS_PREFIX}/onboarding/agent/setup`, body, { params: { agent_id: agentId } })).data;
  },
  // Which Outcome ROI checks a success definition describes (Atlas asks the AI service).
  async suggestOutcomeChecks(agentId, successDefinition) {
    return (await api.post(`${ATLAS_PREFIX}/onboarding/agent/suggest-checks`, { success_definition: successDefinition },
      { params: { agent_id: agentId } })).data;
  },
  // Outcome ROI for one onboarded agent (the list is part of `GET /finops/roi`).
  async getAgentOutcome(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/outcomes/agent`, { params: { ...params, agent_id: agentId } })).data;
  },
  // What is set up for one agent (policies, budget, red team) and how each is doing.
  async getAgentControls(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/agent-controls`, { params: { ...params, agent_id: agentId } })).data;
  },
  // Load one of the agent's policies into the policy engine now. { synced, reason }.
  async loadPolicyIntoEngine(agentId, policyId) {
    return (await api.post(`${ATLAS_PREFIX}/agent-controls/policy-sync`, {}, { params: { agent_id: agentId, policy_id: policyId } })).data;
  },

  // ---- Agents (inventory + Agent 360) ----
  async listAgents(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/agents`, { params })).data;
  },
  // One page of the Inventory's rows, filtered, searched and sorted by the server.
  async listAgentsPage(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/agents/page`, { params })).data;
  },
  async getAgent(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/agents/${encodeURIComponent(agentId)}`, { params })).data;
  },
  async getAgentTimeline(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/agents/${encodeURIComponent(agentId)}/timeline`, { params })).data;
  },
  // Compliance-violation drill-down for one agent: framework breakdown, or the
  // per-framework violation records when `framework` (a framework_id) is passed.
  async getAgentViolations(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/agents/${encodeURIComponent(agentId)}/violations`, { params })).data;
  },
  // Policy-violation drill-down for one agent: per-policy breakdown, or the
  // per-policy violation records when `policy` (a policy_id) is passed.
  async getAgentPolicyViolations(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/agents/${encodeURIComponent(agentId)}/policy-violations`, { params })).data;
  },
  // The source trace document behind a single violation record ({log_id, source_index}).
  async getViolationTrace(agentId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/agents/${encodeURIComponent(agentId)}/violations/trace`, { params })).data;
  },

  // ---- Users (User 360 drill-down) ----
  async listUsers(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/users`, { params })).data;
  },
  async getUser(email, params = {}) {
    // withSurface: when this page is mounted inside a product module the pinned
    // surface travels with the request, so "this person" means their footprint ON
    // THIS SURFACE. Unmounted (Atlas), nothing is added and it stays the
    // cross-surface view it has always been.
    return (await api.get(`${ATLAS_PREFIX}/users/${encodeURIComponent(email)}`,
                          { params: withSurface(params) })).data;
  },

  // ---- Responsible AI (NIST AI RMF posture across every surface) ----
  async getResponsibleAIOverview(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/responsible-ai/overview`, { params })).data;
  },
  async getResponsibleAIPillars(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/responsible-ai/pillars`, { params })).data;
  },
  // Data-driven POV bundle: verdict signals, tested-vs-observed, exposure,
  // guardrail effectiveness, hotspots, and untested-exposure — computed
  // together from the same production telemetry.
  async getResponsibleAIInsights(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/responsible-ai/insights`, { params })).data;
  },
  async getResponsibleAICoverageMatrix(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/responsible-ai/coverage-matrix`, { params })).data;
  },
  // Per-pillar score breakdown + the evidence records behind each component.
  async getResponsibleAIPillarEvidence(pillarId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/responsible-ai/pillars/${encodeURIComponent(pillarId)}/evidence`, { params })).data;
  },
  // The AI system register: one governance record per deployed system.
  async getResponsibleAISystems(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/responsible-ai/systems`, { params })).data;
  },
  async getResponsibleAIFindings(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/responsible-ai/findings`, { params })).data;
  },

  // Optional server-side report. The page can always fall back to the
  // client-side report builder if this endpoint is not yet available.
  async getResponsibleAIReport(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/responsible-ai/report`, { params })).data;
  },

  // ---- Compliance + Audit Report ----
  async getComplianceOverview(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/overview`, { params })).data;
  },
  async getComplianceFrameworks(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/frameworks`, { params })).data;
  },
  async getComplianceControls(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/controls`, { params })).data;
  },
  async getComplianceSurfaces(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/surfaces`, { params })).data;
  },
  async getComplianceTrend(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/trend`, { params })).data;
  },
  async getComplianceAuditReport(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/audit-report`, { params })).data;
  },
  async getComplianceModules(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/modules`, { params })).data;
  },
  /** Current vs the immediately preceding window: { current, previous, window_days }. */
  async getCompliancePeriodComparison(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/period-comparison`, { params })).data;
  },
  async getComplianceUsers(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/users`, { params })).data;
  },
  async getComplianceLogs(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/compliance/logs`, { params })).data;
  },

  // ---- Agent Atlas Policy (cross-surface policy posture) ----
  // The page /agent-atlas/policy calls ONLY these endpoints. Every list and
  // aggregate takes {days} or {start_time, end_time} plus {surface}; list
  // endpoints additionally take their own server-side filters, sort and
  // pagination (see the wiring plan for the frozen contract).
  async getPolicyOverview(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/policy/overview`, { params })).data;
  },
  async getPolicyPlanes(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/policy/planes`, { params })).data;
  },
  async getPolicyActions(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/policy/actions`, { params })).data;
  },
  async listPolicies(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/policy/policies`, { params })).data;
  },
  async getPolicy(policyId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/policy/policies/${encodeURIComponent(policyId)}`, { params })).data;
  },
  // Decisions per day/week by outcome; takes the feed's filters (not cursor).
  async getPolicyTrend(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/policy/trend`, { params })).data;
  },
  async listPolicyDecisions(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/policy/decisions`, { params })).data;
  },
  async getPolicyDecisionTrace(decisionId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/policy/decisions/${encodeURIComponent(decisionId)}/trace`, { params })).data;
  },

  // ---- Adoption (conversations / knowledge gaps / intent, merged across surfaces) ----
  // Each takes {days} or {start_time, end_time} and an optional {surface}. Sessions also take
  // {agent, user, threats, depth, gaps, page, page_size}; intents take {category (repeatable),
  // agent, unclassified, risky_only, page, page_size}.
  async getAdoptionIntent(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/adoption/intent`, { params })).data;
  },
  async getAdoptionKnowledgeGaps(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/adoption/knowledge-gaps`, { params })).data;
  },
  async getAdoptionSessions(params = {}) {
    // repeated keys for the multi-select filters (?drift=a&drift=b), as FastAPI reads a list
    return (await api.get(`${ATLAS_PREFIX}/adoption/sessions`, { params, paramsSerializer: { indexes: null } })).data;
  },
  async getAdoptionSession(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/adoption/session`, { params })).data;
  },
  // Conversations read as traces (Custom Agents): one turn per trace, its spans underneath.
  // The Filters menu's groups (agent, user, threat, model, contains, severity) repeat
  // (agent=a&agent=b), which FastAPI reads as a list — not axios' default `agent[]=`.
  async getTraceConversations(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/adoption/traces/conversations`, { params, paramsSerializer: { indexes: null } })).data;
  },
  async getTraceConversation(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/adoption/traces/conversation`, { params })).data;
  },
  async lookupTraceConversation(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/adoption/traces/lookup`, { params })).data;
  },
  // One span of a conversation's turn, whole (raw attributes with `raw: true`).
  async getTraceSpan(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/adoption/traces/span`, { params })).data;
  },
  // The detection engine's session-drift verdict for one conversation.
  async getTraceDrift(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/adoption/traces/drift`, { params })).data;
  },
  async getAdoptionIntents(params = {}) {
    // `category` repeats (category=a&category=b), which is what FastAPI reads as a list —
    // not axios' default `category[]=`.
    return (await api.get(`${ATLAS_PREFIX}/adoption/intents`, { params, paramsSerializer: { indexes: null } })).data;
  },

  // ---- FinOps (cross-surface Tokenomics / Usage & ROI) ----
  // Token volume only. Cost is deliberately absent: only some surfaces are
  // billed per token, so an org-wide "spend" would be invented. Per-module
  // cost comes from the module page embedded under the surface switcher.
  async getFinOpsSurfaces(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/surfaces`, { params })).data;
  },
  async getFinOpsTrend(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/trend`, { params })).data;
  },
  async getFinOpsCatalog(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/catalog`, { params })).data;
  },

  // --- Budgets & Alerts ------------------------------------------------------
  // Budgets on any scope (org, cost centre, team, person, agent, surface, model) and
  // period, each with where it stands now (`status`). `surface` limits the list to one
  // surface's own budgets, for a module's pinned page.
  async getFinOpsBudgets(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/budgets`, { params })).data;
  },
  async getFinOpsBudget(budgetId, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/budgets/${budgetId}`, { params })).data;
  },
  async createFinOpsBudget(body, params = {}) {
    return (await api.post(`${ATLAS_PREFIX}/finops/budgets`, body, { params })).data;
  },
  async updateFinOpsBudget(budgetId, body, params = {}) {
    return (await api.put(`${ATLAS_PREFIX}/finops/budgets/${budgetId}`, body, { params })).data;
  },
  async deleteFinOpsBudget(budgetId, params = {}) {
    return (await api.delete(`${ATLAS_PREFIX}/finops/budgets/${budgetId}`, { params })).data;
  },
  // Teams, cost centres, people, agents, models and alert channels the editor offers.
  async getBudgetOptions(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/budget-options`, { params })).data;
  },
  async getBudgetEvents(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/budget-events`, { params })).data;
  },
  async getBudgetRequests(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/budget-requests`, { params })).data;
  },
  async createBudgetRequest(body, params = {}) {
    return (await api.post(`${ATLAS_PREFIX}/finops/budget-requests`, body, { params })).data;
  },
  async decideBudgetRequest(requestId, body, params = {}) {
    return (await api.post(`${ATLAS_PREFIX}/finops/budget-requests/${requestId}/decide`, body, { params })).data;
  },
  // --- Tokenomics -----------------------------------------------------------
  // Every surface side by side (spend, people, change), spend by team, cross-surface insights.
  async getFinOpsOverview(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/overview`, { params })).data;
  },
  // Billed vs measured: the vendors' and clouds' bills beside measured spend, unattributed cloud
  // lines, and where each billing source stands.
  async getFinOpsBilling(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/billing`, { params })).data;
  },
  // One surface in depth: by model, agent, product, use case, tier, person; cache; projection.
  async getFinOpsTokenomics(surface, params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/tokenomics`, { params: { ...params, surface } })).data;
  },
  // Models in use at list price and at what this organization pays; its discounts and own prices.
  async getFinOpsPrices(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/prices`, { params })).data;
  },
  async saveFinOpsPrices(body, params = {}) {
    return (await api.put(`${ATLAS_PREFIX}/finops/prices`, body, { params })).data;
  },
  // --- ROI & Usage -----------------------------------------------------------
  // Every surface's uses, people, time saved and usage cost, the value tiers, adoption depth and
  // Outcome ROI per agent; with `surface`, that surface in depth.
  async getFinOpsRoi(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/roi`, { params })).data;
  },
  // Hourly cost, minutes saved per surface, difficulty weights, headcount. PUT takes any subset.
  async getFinOpsAssumptions(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/assumptions`, { params })).data;
  },
  async saveFinOpsAssumptions(body, params = {}) {
    return (await api.put(`${ATLAS_PREFIX}/finops/assumptions`, body, { params })).data;
  },
  // --- Licensing -------------------------------------------------------------
  // Seats bought against people active (30 days) per vendor, idle cost, seats to reclaim,
  // people paying twice, the next renewal; with `surface`, that surface only.
  async getFinOpsLicensing(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/licensing`, { params })).data;
  },
  // Licence contracts: seats bought, price per seat a month, term, notice deadline, owner.
  async getFinOpsContracts(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/contracts`, { params })).data;
  },
  async createFinOpsContract(body, params = {}) {
    return (await api.post(`${ATLAS_PREFIX}/finops/contracts`, body, { params })).data;
  },
  async updateFinOpsContract(contractId, body, params = {}) {
    return (await api.put(`${ATLAS_PREFIX}/finops/contracts/${contractId}`, body, { params })).data;
  },
  async deleteFinOpsContract(contractId, params = {}) {
    return (await api.delete(`${ATLAS_PREFIX}/finops/contracts/${contractId}`, { params })).data;
  },
  // The FinOps calendar (time zone, week, budget month, fiscal year) and alert defaults.
  async getFinOpsSettings(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/finops/settings`, { params })).data;
  },
  async saveFinOpsSettings(body, params = {}) {
    return (await api.put(`${ATLAS_PREFIX}/finops/settings`, body, { params })).data;
  },

  // ---- Cross-surface intelligence ----
  async getThreatSurfaceHeatmap(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/intel/threat-surface-heatmap`, { params })).data;
  },
  async getDenyShadowFlow(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/intel/deny-shadow-flow`, { params })).data;
  },
  async getBlastRadius(params = {}) {
    return (await api.get(`${ATLAS_PREFIX}/intel/blast-radius`, { params })).data;
  },

  // ---- Graph (Neo4j entity graph) ----
  // Relationship views, not aggregations: each of these is one traversal, where the
  // equivalent in OpenSearch would be several queries plus an app-side join. The graph
  // answers who is connected to what; use the other endpoints for per-event detail.
  //
  // Counts come from edges — an alert or an app usage increments observation_count on a
  // single relationship — so `uses`/`alerts`/`impacts` are event totals, not node counts.
  async getGraphStats(params = {}) {
    return graphLensService.graphStats();
  },
  // Topology for the node-link canvas: { nodes, edges }, highest-degree first.
  async getGraphNetwork(params = {}) {
    return graphLensService.graphNetwork(params);
  },
  async getGraphAgents(params = {}) {
    return graphLensService.graphAgents(params);
  },
  // Origins present in the graph (gemini_enterprise, copilot_studio, cursor, ...).
  async getGraphSurfaces(params = {}) {
    return graphLensService.graphSurfaces();
  },
  // n-hop subgraph around one agent — its lineage as a graph, not a list.
  async getGraphAgentNeighborhood(agentId, params = {}) {
    return graphLensService.graphNeighborhood(agentId, params);
  },
  // Same, keyed on any node id — lets a UI re-focus on whatever was clicked instead of
  // only ever expanding outward from an agent.
  async getGraphNodeNeighborhood(nodeId, params = {}) {
    return graphLensService.graphNeighborhood(nodeId, params);
  },
  async getGraphShadowAI(params = {}) {
    return graphLensService.graphShadowAi(params);
  },
  async getGraphModels(params = {}) {
    return graphLensService.graphModels();
  },
  async getGraphFrameworkExposure(params = {}) {
    return graphLensService.graphFrameworkExposure();
  },
  async getGraphDetectionMap(params = {}) {
    return graphLensService.graphDetectionMap();
  },
  async getGraphPolicyViolations(params = {}) {
    return graphLensService.graphPolicyViolations(params);
  },
  async getGraphAlerts(params = {}) {
    return graphLensService.graphAlerts();
  },
  async getGraphRiskyAgents(params = {}) {
    return graphLensService.graphRiskyAgents(params);
  },
  // Blast radius from the graph: the actual downstream node set each agent reaches,
  // not the event-volume score the OpenSearch /intel/blast-radius can only approximate.
  async getGraphBlastRadius(params = {}) {
    return { items: await graphLensService.graphReachRanked(params) };
  },
  async getGraphAgentLineage(agentId, params = {}) {
    return graphLensService.graphLineage(agentId);
  },
};

export default agentAtlasAPI;
